import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import {
  EmailStatuses,
  PagedList,
  SearchEmailFilter,
  SearchEmailItemResponse,
  getCompanyEmails,
  resendEmail,
} from 'core'

import { Button, Card, Col, DefaultInputV2, DefaultSelectV2, EmailsTable, Icon, Row } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'

const Emails = () => {
  const { t: translate } = useTranslation()

  const [filter, setFilter] = useState<SearchEmailFilter>({
    pageNumber: 1,
    pageSize: 25,
    orderColumn: 'InitiatedOn',
    orderDescending: false,
  })

  const { data: emails, isLoading: queryIsLoading, sendQuery } = useQueryRequestV2<PagedList<SearchEmailItemResponse>>()
  const { sendCommand, isLoading: commandIsLoading } = useCommandRequestV2(resendEmail)

  useEffect(() => {
    ;(async () => await sendQuery(() => getCompanyEmails(filter)))()
  }, [filter])

  const availableStatuses = [
    { name: 'All statuses', id: null },
    ...EmailStatuses.map((x) => ({ name: x.name, id: x.id })),
  ]

  return (
    <Card title={translate('admin-dashboard.left-navigation.messages.email-messages.header')}>
      <Row>
        <Col className="w-100">
          <Formik
            enableReinitialize={true}
            validationSchema={Yup.object({})}
            initialValues={filter}
            onSubmit={(values) => {
              setFilter({
                ...filter,
                status: values.status,
                q: values.q,
              })
            }}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit}>
                <Row gutter={16}>
                  <Col span={8}>
                    <DefaultInputV2
                      suffix={<Icon type="fal fa-search" fontAwesome />}
                      onKeyUp={async (e: any) => {
                        if (e.keyCode == 13) {
                          await formik.submitForm()
                        }
                      }}
                      className="mr-8"
                      placeholder={translate('csi-dashboard.email-search.input-placeholder')}
                      name={'q'}
                    />
                  </Col>
                  <Col span={5}>
                    <DefaultSelectV2
                      name="status"
                      placeholder={'Select status'}
                      label={null}
                      items={availableStatuses}
                    />
                  </Col>
                  <Col span={10}>
                    <Button
                      type="primary"
                      className={'mr-16'}
                      htmlType={'submit'}
                      disabled={queryIsLoading}
                      loading={queryIsLoading}
                    >
                      {translate('general.search')}
                    </Button>

                    <Button
                      htmlType={'button'}
                      onClick={() => {
                        setFilter({
                          ...filter,
                          q: null,
                          status: null,
                        })
                      }}
                      disabled={queryIsLoading}
                      loading={queryIsLoading}
                    >
                      Reset Filter
                    </Button>
                  </Col>
                </Row>
              </form>
            )}
          </Formik>

          <Row className="mt-16">
            <EmailsTable
              isResponsive
              showAction
              actionLabel="Resend"
              emails={emails}
              loadingEmails={queryIsLoading}
              onChange={(filter) => {
                setFilter({
                  ...filter,
                  orderDescending: filter.orderDescending,
                  pageSize: filter.pageSize,
                  orderColumn: filter.orderColumn,
                  pageNumber: filter.pageNumber,
                })
              }}
              actionIsLoading={commandIsLoading}
              onActionClick={async (id) => await sendCommand(id)}
              onRow={(id) => ({ onDoubleClick: async () => await sendCommand(id) })}
            />
          </Row>
        </Col>
      </Row>
    </Card>
  )
}

export default Emails
